/**
 * Voice Provider Router - Phase 11.2
 * 
 * Intelligent routing layer for TTS/STT providers with automatic fallback.
 * Selects between ElevenLabs (premium) and Web Speech API (fallback).
 */

import { supabase, SUPABASE_PROJECT_URL } from '@/integrations/supabase/client';
import { tts, TTSOptions } from '@/lib/textToSpeech';

// Use proper project URL constant
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

export type TTSProvider = 'elevenlabs' | 'webspeech';
export type STTProvider = 'elevenlabs' | 'whisper';

export interface VoiceProviderConfig {
  preferredTTSProvider: TTSProvider | 'auto';
  preferredSTTProvider: STTProvider | 'auto';
  enableFallback: boolean;
  elevenLabsEnabled: boolean;
}

export interface UnifiedTTSOptions extends TTSOptions {
  voiceId?: string;
  forceProvider?: TTSProvider;
}

export interface TTSResult {
  success: boolean;
  provider: TTSProvider;
  error?: string;
}

export interface STTResult {
  success: boolean;
  provider: STTProvider;
  text: string;
  words?: Array<{ word: string; start: number; end: number; confidence: number }>;
  confidence?: number;
  error?: string;
}

// Default config when not loaded from DB
const DEFAULT_CONFIG: VoiceProviderConfig = {
  preferredTTSProvider: 'auto',
  preferredSTTProvider: 'auto',
  enableFallback: true,
  elevenLabsEnabled: false,
};

let cachedConfig: VoiceProviderConfig | null = null;
let configLoadPromise: Promise<VoiceProviderConfig> | null = null;

/**
 * Load voice provider configuration from database via REST API
 */
async function loadConfig(): Promise<VoiceProviderConfig> {
  if (cachedConfig) return cachedConfig;
  
  if (configLoadPromise) return configLoadPromise;
  
  configLoadPromise = (async () => {
    try {
      const response = await fetch(
        `${SUPABASE_PROJECT_URL}/rest/v1/elevenlabs_config?select=is_enabled&limit=1`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
        }
      );
      
      if (response.ok) {
        const rows = await response.json();
        if (rows && rows.length > 0) {
          cachedConfig = {
            ...DEFAULT_CONFIG,
            elevenLabsEnabled: Boolean(rows[0].is_enabled),
          };
          return cachedConfig;
        }
      }
      
      console.log('[VoiceProviderRouter] No config found, using defaults');
      return DEFAULT_CONFIG;
    } catch (err) {
      console.warn('[VoiceProviderRouter] Failed to load config:', err);
      return DEFAULT_CONFIG;
    } finally {
      configLoadPromise = null;
    }
  })();
  
  return configLoadPromise;
}

/**
 * Invalidate cached config (call when settings change)
 */
export function invalidateVoiceConfig(): void {
  cachedConfig = null;
  configLoadPromise = null;
}

/**
 * Check if ElevenLabs is available and enabled
 */
async function isElevenLabsAvailable(): Promise<boolean> {
  const config = await loadConfig();
  return config.elevenLabsEnabled;
}

/**
 * Select the best TTS provider based on config and availability
 */
export async function selectTTSProvider(
  forceProvider?: TTSProvider
): Promise<TTSProvider> {
  if (forceProvider) return forceProvider;
  
  const config = await loadConfig();
  
  if (config.preferredTTSProvider === 'elevenlabs') {
    return config.elevenLabsEnabled ? 'elevenlabs' : 'webspeech';
  }
  
  if (config.preferredTTSProvider === 'webspeech') {
    return 'webspeech';
  }
  
  // Auto mode: prefer ElevenLabs if available
  if (config.elevenLabsEnabled) {
    return 'elevenlabs';
  }
  
  return 'webspeech';
}

/**
 * Select the best STT provider based on config
 */
export async function selectSTTProvider(): Promise<STTProvider> {
  const config = await loadConfig();
  
  if (config.preferredSTTProvider === 'elevenlabs' && config.elevenLabsEnabled) {
    return 'elevenlabs';
  }
  
  // Default to Whisper for STT
  return 'whisper';
}

/**
 * Speak text using ElevenLabs TTS
 */
async function speakWithElevenLabs(
  text: string,
  options?: UnifiedTTSOptions
): Promise<TTSResult> {
  try {
    const response = await fetch(
      `${SUPABASE_PROJECT_URL}/functions/v1/elevenlabs-tts`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          text,
          voiceId: options?.voiceId,
          speed: options?.rate,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `TTS request failed: ${response.status}`);
    }

    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    
    return new Promise((resolve) => {
      const audio = new Audio(audioUrl);
      audio.volume = options?.volume ?? 1;
      audio.playbackRate = options?.rate ?? 1;
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        resolve({ success: true, provider: 'elevenlabs' });
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        resolve({ 
          success: false, 
          provider: 'elevenlabs',
          error: 'Audio playback failed'
        });
      };
      
      audio.play().catch((err) => {
        URL.revokeObjectURL(audioUrl);
        resolve({
          success: false,
          provider: 'elevenlabs',
          error: err.message || 'Failed to play audio',
        });
      });
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.error('[VoiceProviderRouter] ElevenLabs TTS error:', message);
    return { success: false, provider: 'elevenlabs', error: message };
  }
}

/**
 * Speak text using Web Speech API
 */
function speakWithWebSpeech(
  text: string,
  options?: UnifiedTTSOptions
): Promise<TTSResult> {
  return new Promise((resolve) => {
    const success = tts.speak(text, {
      rate: options?.rate,
      pitch: options?.pitch,
      volume: options?.volume,
      voiceIndex: options?.voiceIndex,
    });
    
    if (!success) {
      resolve({ 
        success: false, 
        provider: 'webspeech',
        error: 'Web Speech API not supported or text empty'
      });
      return;
    }
    
    tts.onEnd(() => {
      resolve({ success: true, provider: 'webspeech' });
    });
  });
}

/**
 * Speak text with automatic provider selection and fallback
 */
export async function speak(
  text: string,
  options?: UnifiedTTSOptions
): Promise<TTSResult> {
  const provider = await selectTTSProvider(options?.forceProvider);
  const config = await loadConfig();
  
  console.log(`[VoiceProviderRouter] Speaking with ${provider}`);
  
  if (provider === 'elevenlabs') {
    const result = await speakWithElevenLabs(text, options);
    
    // Fallback to Web Speech if ElevenLabs fails and fallback is enabled
    if (!result.success && config.enableFallback) {
      console.log('[VoiceProviderRouter] ElevenLabs failed, falling back to Web Speech');
      return speakWithWebSpeech(text, options);
    }
    
    return result;
  }
  
  return speakWithWebSpeech(text, options);
}

/**
 * Stop any active speech
 */
export function stopSpeaking(): void {
  tts.stop();
  // Note: ElevenLabs audio is controlled via Audio element in the hook
}

/**
 * Check if TTS is supported
 */
export function isTTSSupported(): boolean {
  return tts.isSupported();
}

/**
 * Get current voice provider config
 */
export async function getVoiceConfig(): Promise<VoiceProviderConfig> {
  return loadConfig();
}
